import { Restaurant, Violation } from '../types'

const commonViolations: Record<
  string,
  Omit<Violation, 'id' | 'originalNote'>
> = {
  temp: {
    code: '3-501.16',
    severity: 'Critical',
    title: 'Food stored at unsafe temperature',
    explanation:
      'Food was kept at a temperature where harmful bacteria can grow quickly. Cold food must be kept very cold, and hot food very hot to be safe to eat.',
  },
  handwashing: {
    code: '6-301.12',
    severity: 'High',
    title: 'Handwashing station not accessible or missing supplies',
    explanation:
      'Employees did not have easy access to soap, paper towels, or warm water to wash their hands. Proper handwashing is the #1 way to prevent foodborne illness.',
  },
  pests: {
    code: '6-501.111',
    severity: 'Critical',
    title: 'Evidence of pest activity',
    explanation:
      'Inspectors found signs of insects or rodents in the facility. This poses a severe risk of contamination to food and surfaces.',
  },
  surfaces: {
    code: '4-601.11',
    severity: 'Medium',
    title: 'Food contact surfaces not properly sanitized',
    explanation:
      'Cutting boards, counters, or utensils were not cleaned with the correct concentration of sanitizer, which can lead to cross-contamination between raw and cooked foods.',
  },
  storage: {
    code: '3-302.11',
    severity: 'High',
    title: 'Raw food stored above ready-to-eat food',
    explanation:
      "Raw meat was stored on a shelf above foods that won't be cooked (like salads). If the raw meat drips, it can contaminate the safe food with dangerous bacteria.",
  },
  plumbing: {
    code: '5-205.15',
    severity: 'Low',
    title: 'Minor plumbing leak',
    explanation:
      'A sink or pipe had a slow leak. While not an immediate food safety threat, standing water can attract pests over time.',
  },
  hair: {
    code: '2-402.11',
    severity: 'Low',
    title: 'Employee missing hair restraint',
    explanation:
      'A food worker was not wearing a hat or hairnet. This is a minor violation to prevent hair from falling into food.',
  },
}

export const mockRestaurants: Restaurant[] = [
  {
    id: 'r1',
    name: 'The Rustic Table',
    cuisine: 'American',
    address: '124 Main St, Springfield',
    currentGrade: 'A',
    lastInspectionDate: '2023-10-15',
    lat: 39.799,
    lng: -89.644,
    inspections: [
      {
        id: 'i1-1',
        date: '2023-10-15',
        score: 96,
        grade: 'A',
        status: 'Pass',
        violations: [
          {
            id: 'v1',
            ...commonViolations.plumbing,
            originalNote:
              'Observed slow drip from prep sink faucet in rear kitchen.',
          },
          {
            id: 'v2',
            ...commonViolations.hair,
            originalNote:
              'One prep cook observed without proper hair restraint during active food prep.',
          },
        ],
      },
      {
        id: 'i1-2',
        date: '2023-04-10',
        score: 98,
        grade: 'A',
        status: 'Pass',
        violations: [],
      },
    ],
  },
  {
    id: 'r2',
    name: 'Bella Napoli Pizzeria',
    cuisine: 'Italian',
    address: '890 Oak Ave, Springfield',
    currentGrade: 'B',
    lastInspectionDate: '2023-11-02',
    lat: 39.795,
    lng: -89.65,
    inspections: [
      {
        id: 'i2-1',
        date: '2023-11-02',
        score: 84,
        grade: 'B',
        status: 'Conditional Pass',
        violations: [
          {
            id: 'v3',
            ...commonViolations.surfaces,
            originalNote:
              'Deli slicer had dried food debris on blade guard from previous day.',
          },
          {
            id: 'v4',
            ...commonViolations.handwashing,
            originalNote:
              'Front handwash sink blocked by trash can and missing paper towels.',
          },
        ],
      },
      {
        id: 'i2-2',
        date: '2023-05-18',
        score: 92,
        grade: 'A',
        status: 'Pass',
        violations: [
          {
            id: 'v5',
            ...commonViolations.hair,
            originalNote: 'Server handling open food without hair tied back.',
          },
        ],
      },
    ],
  },
  {
    id: 'r3',
    name: 'Golden Dragon',
    cuisine: 'Chinese',
    address: '455 Riverside Dr, Springfield',
    currentGrade: 'C',
    lastInspectionDate: '2023-12-01',
    lat: 39.788,
    lng: -89.638,
    inspections: [
      {
        id: 'i3-1',
        date: '2023-12-01',
        score: 72,
        grade: 'C',
        status: 'Conditional Pass',
        violations: [
          {
            id: 'v6',
            ...commonViolations.temp,
            originalNote:
              'Cooked rice stored at room temperature (68F) for over 4 hours. Product discarded.',
          },
          {
            id: 'v7',
            ...commonViolations.storage,
            originalNote:
              'Raw chicken stored on top shelf of walk-in cooler above uncovered produce.',
          },
          {
            id: 'v8',
            ...commonViolations.surfaces,
            originalNote: 'Sanitizer bucket at 0ppm concentration.',
          },
        ],
      },
      {
        id: 'i3-2',
        date: '2023-06-15',
        score: 88,
        grade: 'B',
        status: 'Pass',
        violations: [
          {
            id: 'v9',
            ...commonViolations.temp,
            originalNote:
              'Prep cooler holding ambient temp of 45F. Adjusted during inspection.',
          },
        ],
      },
    ],
  },
  {
    id: 'r4',
    name: 'El Camino Taqueria',
    cuisine: 'Mexican',
    address: '2210 5th St, Springfield',
    currentGrade: 'A',
    lastInspectionDate: '2023-11-20',
    lat: 39.783,
    lng: -89.655,
    inspections: [
      {
        id: 'i4-1',
        date: '2023-11-20',
        score: 99,
        grade: 'A',
        status: 'Pass',
        violations: [
          {
            id: 'v10',
            ...commonViolations.plumbing,
            originalNote: 'Minor leak at mop sink.',
          },
        ],
      },
    ],
  },
  {
    id: 'r5',
    name: 'Sakura Sushi',
    cuisine: 'Japanese',
    address: '770 Market St, Springfield',
    currentGrade: 'A',
    lastInspectionDate: '2023-10-05',
    lat: 39.792,
    lng: -89.647,
    inspections: [
      {
        id: 'i5-1',
        date: '2023-10-05',
        score: 95,
        grade: 'A',
        status: 'Pass',
        violations: [
          {
            id: 'v11',
            ...commonViolations.surfaces,
            originalNote:
              'Cutting board deeply scored and cannot be properly sanitized. Needs replacement.',
          },
        ],
      },
    ],
  },
  {
    id: 'r6',
    name: 'Burger Joint',
    cuisine: 'American',
    address: '100 Highway 1, Springfield',
    currentGrade: 'F',
    lastInspectionDate: '2023-12-10',
    lat: 39.785,
    lng: -89.66,
    inspections: [
      {
        id: 'i6-1',
        date: '2023-12-10',
        score: 55,
        grade: 'F',
        status: 'Fail',
        violations: [
          {
            id: 'v12',
            ...commonViolations.pests,
            originalNote:
              'Live roaches observed in dry storage area and under prep tables. Facility closed.',
          },
          {
            id: 'v13',
            ...commonViolations.temp,
            originalNote:
              'Walk-in cooler completely failed. All TCS foods temped at 55F-60F. Discarded.',
          },
          {
            id: 'v14',
            ...commonViolations.handwashing,
            originalNote: 'No hot water available at any handwashing sink.',
          },
        ],
      },
      {
        id: 'i6-2',
        date: '2023-05-22',
        score: 82,
        grade: 'B',
        status: 'Conditional Pass',
        violations: [
          {
            id: 'v15',
            ...commonViolations.pests,
            originalNote: 'Old rodent droppings found in rear storage room.',
          },
        ],
      },
    ],
  },
  {
    id: 'r7',
    name: 'Spice Route',
    cuisine: 'Indian',
    address: '3400 Central Ave, Springfield',
    currentGrade: 'A',
    lastInspectionDate: '2023-09-12',
    lat: 39.797,
    lng: -89.635,
    inspections: [
      {
        id: 'i7-1',
        date: '2023-09-12',
        score: 94,
        grade: 'A',
        status: 'Pass',
        violations: [
          {
            id: 'v16',
            ...commonViolations.storage,
            originalNote: 'Uncovered containers of spices in dry storage.',
          },
        ],
      },
    ],
  },
  {
    id: 'r8',
    name: 'Mediterranean Grill',
    cuisine: 'Mediterranean',
    address: '550 West End, Springfield',
    currentGrade: 'B',
    lastInspectionDate: '2023-11-15',
    lat: 39.79,
    lng: -89.652,
    inspections: [
      {
        id: 'i8-1',
        date: '2023-11-15',
        score: 86,
        grade: 'B',
        status: 'Pass',
        violations: [
          {
            id: 'v17',
            ...commonViolations.temp,
            originalNote: 'Hummus on buffet line temped at 48F. Placed on ice.',
          },
          {
            id: 'v18',
            ...commonViolations.hair,
            originalNote: 'Multiple employees missing hairnets.',
          },
        ],
      },
    ],
  },
]
